import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import { DarijaBuddy } from './DarijaBuddy';
import { useTranslation } from './i18n/LanguageContext';
import Logo from './components/Logo';

const ModernLayout = ({ 
  children, 
  currentView, 
  setCurrentView,
  activeProfileTab, // Track which profile tab is active
  isDarkMode,
  onToggleDarkMode,
  currentUser,
  isAdminAuthenticated,
  onLogout,
  userPlan,
  setInitialProfileTab,
  hasPaidPlan,
  userToken,
  showNotification,
  onOpenFavorites,
  onOpenProgress,
  onOpenCategories,
  onOpenSuggest,
  appSettings = {},
  travelToolsSettings = { is_visible: true, is_pro_only: false },
  musicSettings = { pageVisible: true, isProOnly: false },
  culturePodSettings = { pageVisible: true, isProOnly: false },
  decoderSettings = { is_visible: true, is_pro_only: false },
  dictionarySettings = { is_visible: true, is_pro_only: false },
  amazighSettings = { is_visible: true, is_pro_only: false },
  verbExplorerSettings = { is_visible: false },
  currentMood = null, // For Darija Buddy mood awareness
  weatherRefreshTrigger = 0, // Increment to refresh weather
  navDesign = null, // Navigation design settings from admin
  navDesignLoaded = false, // True when navDesign has resolved (cache hit or fetch). Used to gate icon-slot content so emoji-fallback doesn't flicker into image on first paint.
  activeGame = null // Hide Buddy when a game is active
}) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Hide the bottom mobile nav while an input/textarea is focused so the iOS
  // keyboard doesn't sit behind it covering the input.
  const [isInputFocused, setIsInputFocused] = useState(false);
  React.useEffect(() => {
    const onFocusIn = (e) => {
      const tag = e.target?.tagName;
      const editable = e.target?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) setIsInputFocused(true);
    };
    const onFocusOut = (e) => {
      const tag = e.target?.tagName;
      const editable = e.target?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) setIsInputFocused(false);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDashboardSubmenuOpen, setIsDashboardSubmenuOpen] = useState(false);
  const [isHelpSubmenuOpen, setIsHelpSubmenuOpen] = useState(false);
  const [isDashboardSubmenuOpenMobile, setIsDashboardSubmenuOpenMobile] = useState(false);
  const [isHelpSubmenuOpenMobile, setIsHelpSubmenuOpenMobile] = useState(false);
  const [holidayNotifications, setHolidayNotifications] = useState(() => {
    // Cache holiday notifications so the card stops hiding/showing on Ctrl+R.
    // Same pattern as weather + appSettings.
    try { const c = localStorage.getItem('holidayNotificationsCache'); if (c) return JSON.parse(c); } catch {}
    return { has_notifications: false, count: 0, holidays: [] };
  });
  const [holidayPageSettings, setHolidayPageSettings] = useState({});
  // currentUser is set asynchronously by App.js after JWT validates against
  // the backend. We need a SYNCHRONOUS "is probably logged in" signal so the
  // weather/time row renders from frame 1 (no hide→show on Ctrl+R). If the
  // stored token is invalid the user gets logged out shortly — they briefly
  // see the empty chrome, no harm done.
  const hasStoredAuth = (() => {
    try { return !!localStorage.getItem('user_token'); } catch { return false; }
  })();
  const showLoggedInChrome = !!currentUser || hasStoredAuth;

  // Read cached weather + settings synchronously so the pill renders from
  // frame 1 instead of hide→show after fetches resolve. Cache is updated
  // after every successful weather fetch (see fetchWeather below).
  const [weatherData, setWeatherData] = useState(() => {
    try { const c = localStorage.getItem('weatherDataCache'); if (c) return JSON.parse(c); } catch {}
    return null;
  });
  const [weatherSettings, setWeatherSettings] = useState(() => {
    try { const c = localStorage.getItem('weatherSettingsCache'); if (c) return JSON.parse(c); } catch {}
    return { enabled: false };
  });
  const [moroccoTime, setMoroccoTime] = useState(() => {
    // Compute synchronously on first render so the time slot is never empty
    try {
      return new Date().toLocaleTimeString('en-GB', {
        timeZone: 'Africa/Casablanca',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch { return ''; }
  });
  
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Navigation design library — resolve active design
  const activeNavDesign = (() => {
    if (!navDesign || navDesign.active_design_id === 'classic') return null;
    return (navDesign.custom_designs || []).find(d => d.id === navDesign.active_design_id) || null;
  })();
  const nd = activeNavDesign; // shorthand

  // Dark mode ALWAYS uses standard dark colors — design colors are light mode only
  const navBgType = nd?.bg_type || 'gradient';
  const navFrom = isDarkMode ? '#1f2937' : (nd?.light_gradient_from || '#2563eb');
  const navVia = isDarkMode ? '#1f2937' : (nd?.light_gradient_via || '#1d4ed8');
  const navTo = isDarkMode ? '#111827' : (nd?.light_gradient_to || '#1e3a8a');
  const navSolidBg = isDarkMode ? '#1f2937' : (nd?.light_solid_bg || '#ffffff');
  const navTextColor = isDarkMode ? '#d1d5db' : (nd?.light_text_color || '#e0e7ff');
  const navGlassEnabled = !isDarkMode && (nd?.glass_enabled || false);
  const navGlassBlur = nd?.glass_blur ?? 10;
  const navGlassOpacity = nd?.glass_opacity ?? 80;
  const navSidebarWidth = nd?.sidebar_width ?? 288;
  const navCollapsedWidth = 80;
  const navActiveStyle = nd?.active_style || 'rounded';
  const navHoverBg = isDarkMode ? 'rgba(107,114,128,0.5)' : (nd?.hover_bg_light || 'rgba(255,255,255,0.1)');

  const opacityToHex = (pct) => {
    const val = Math.round((pct / 100) * 255);
    return val.toString(16).padStart(2, '0');
  };

  // Build sidebar background style — dark mode always standard dark
  const sidebarBgStyle = (() => {
    if (isDarkMode) {
      return { background: 'linear-gradient(to bottom, #1f2937, #1f2937, #111827)' };
    }
    if (navBgType === 'solid' && nd) {
      if (navGlassEnabled) {
        return { backdropFilter: `blur(${navGlassBlur}px)`, WebkitBackdropFilter: `blur(${navGlassBlur}px)`, background: `${navSolidBg}${opacityToHex(navGlassOpacity)}` };
      }
      return { background: navSolidBg };
    }
    if (navGlassEnabled && nd) {
      return { backdropFilter: `blur(${navGlassBlur}px)`, WebkitBackdropFilter: `blur(${navGlassBlur}px)`, background: `linear-gradient(to bottom, ${navFrom}${opacityToHex(navGlassOpacity)}, ${navVia}${opacityToHex(navGlassOpacity)}, ${navTo}${opacityToHex(navGlassOpacity)})` };
    }
    return { background: `linear-gradient(to bottom, ${navFrom}, ${navVia}, ${navTo})` };
  })();

  // Active item style — dark mode always standard
  const navActiveBg = isDarkMode ? '#4b5563' : (nd?.active_bg_light || '#ffffff');
  const navActiveText = isDarkMode ? '#ffffff' : (nd?.active_text_light || '#2563eb');
  const activeItemStyle = { backgroundColor: navActiveBg, color: navActiveText };

  // Custom menu item labels/icons from admin
  const menuItemOverrides = navDesign?.menu_items || {};
  const submenuOverrides = navDesign?.submenu_items || {};
  const cssSize = (v) => { if (!v) return null; v = String(v).trim(); if (!v) return null; if (/^[\d.]+$/.test(v)) return v + 'px'; return v; };
  const globalIconSize = cssSize(navDesign?.nav_icon_size);
  const globalIconSizeCollapsed = cssSize(navDesign?.nav_icon_size_collapsed);

  // Update Morocco time every minute
  React.useEffect(() => {
    const updateMoroccoTime = () => {
      const now = new Date();
      const moroccoTimeStr = now.toLocaleTimeString('en-GB', {
        timeZone: 'Africa/Casablanca',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      setMoroccoTime(moroccoTimeStr);
    };
    
    updateMoroccoTime(); // Initial call
    const interval = setInterval(updateMoroccoTime, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);

  // City abbreviations for compact display
  const cityAbbrev = {
    'Casablanca': 'Casa', 'Marrakech': 'Marra', 'Rabat': 'Rabat', 'Tangier': 'Tanger',
    'Fes': 'Fes', 'Agadir': 'Agadir', 'Meknes': 'Meknes', 'Oujda': 'Oujda',
    'Mohammedia': 'Moham', 'Essaouira': 'Essao', 'Chefchaouen': 'Chefch', 'Ouarzazate': 'Ouarza',
    'Kenitra': 'Kenitra', 'Tetouan': 'Tetouan', 'Safi': 'Safi', 'El Jadida': 'El Jad',
    'Nador': 'Nador', 'Asilah': 'Asilah', 'Larache': 'Larache', 'Taza': 'Taza',
    'Settat': 'Settat', 'Errachidia': 'Errach'
  };

  // Fetch weather data for logged-in users
  const fetchWeather = React.useCallback(async () => {
    if (!currentUser || !userToken) return;
    
    try {
      // First check if weather is enabled globally
      const settingsRes = await fetch(`${BACKEND_URL}/api/weather/settings/public`);
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setWeatherSettings(settings);
        try { localStorage.setItem('weatherSettingsCache', JSON.stringify(settings)); } catch {}

        if (!settings.enabled) {
          // User has weather off — clear stale cache so empty pill doesn't render next refresh
          try { localStorage.removeItem('weatherDataCache'); } catch {}
          return;
        }
        
        // Get user's preferred city
        let city = settings.default_city || 'Casablanca';
        let userWeatherEnabled = true;
        
        // Try to get user's preferred settings (may fail if token issue)
        if (userToken) {
          try {
            const userRes = await fetch(`${BACKEND_URL}/api/user/weather-settings`, {
              headers: { 'Authorization': `Bearer ${userToken}` }
            });
            
            if (userRes.ok) {
              const userData = await userRes.json();
              if (userData.weather_city) city = userData.weather_city;
              userWeatherEnabled = userData.weather_enabled !== false;
            }
          } catch (err) {
            console.log('[Weather] Error fetching user settings, using defaults');
          }
        }
        
        if (!userWeatherEnabled) {
          setWeatherData(null);
          return;
        }
        
        // Fetch weather for the city
        const weatherRes = await fetch(`${BACKEND_URL}/api/weather/${encodeURIComponent(city)}`);
        if (weatherRes.ok) {
          const data = await weatherRes.json();
          setWeatherData(data);
          try { localStorage.setItem('weatherDataCache', JSON.stringify(data)); } catch {}
        }
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
    }
  }, [BACKEND_URL, currentUser, userToken]);

  // Initial fetch and periodic refresh
  React.useEffect(() => {
    fetchWeather();
    // Refresh weather every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeather]);

  // Listen for weather refresh events (from profile settings)
  React.useEffect(() => {
    const handleWeatherRefresh = () => {
      fetchWeather();
    };
    
    window.addEventListener('weatherSettingsChanged', handleWeatherRefresh);
    return () => window.removeEventListener('weatherSettingsChanged', handleWeatherRefresh);
  }, [fetchWeather]);

  // Check for holiday notifications + load page settings
  React.useEffect(() => {
    const checkHolidayNotifications = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/holidays/notification-check`);
        if (response.ok) {
          const data = await response.json();
          setHolidayNotifications(data);
          try { localStorage.setItem('holidayNotificationsCache', JSON.stringify(data)); } catch {}
        }
      } catch (error) {
        console.error('Error checking holiday notifications:', error);
      }
    };

    const loadHolidayPageSettings = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/holidays/page-settings`);
        if (res.ok) {
          const data = await res.json();
          setHolidayPageSettings(data);
        }
      } catch {}
    };

    checkHolidayNotifications();
    loadHolidayPageSettings();
    // Check every hour
    const interval = setInterval(checkHolidayNotifications, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [BACKEND_URL]);

  // Main Navigation Items - Flat structure for desktop left sidebar
  const handleMusicClick = () => {
    window.history.pushState({}, '', '/music'); setCurrentView('music');
  };

  const handleCulturePodClick = () => {
    if (culturePodSettings.isProOnly && !hasPaidPlan) {
      window.history.pushState({}, '', '/membership'); setCurrentView('profile'); setInitialProfileTab('membership');
    } else {
      window.history.pushState({}, '', '/culture-pod'); setCurrentView('culturePod');
    }
  };

  const sidebarNavigationItems = [
    { id: 'translator', label: t('translation'), icon: '🌐', action: () => { window.history.pushState({}, '', '/translation'); setCurrentView('translator'); } },
    { id: 'dictionary', label: t('dictionary'), icon: '📖', action: () => { window.history.pushState({}, '', '/dictionary'); setCurrentView('dictionary'); } },
    { id: 'decoder', label: t('decoder_title'), icon: '🔮', action: () => { window.history.pushState({}, '', '/decoder'); setCurrentView('decoder'); } },
    { id: 'amazigh-translation', label: t('nav_amazigh'), icon: 'ⵣ', action: () => { window.history.pushState({}, '', '/amazigh-translation'); setCurrentView('amazigh-translation'); } },
    { id: 'my-daily-words', label: t('nav_daily_words'), icon: '📚', action: () => { window.history.pushState({}, '', '/my-daily-words'); setCurrentView('my-daily-words'); } },
    { id: 'traveltools', label: t('travel_tools'), icon: '🇲🇦', action: () => { window.history.pushState({}, '', '/travel-tools'); setCurrentView('traveltools'); } },
    { id: 'music', label: t('music'), icon: '🎵', action: handleMusicClick },
    { id: 'culturePod', label: t('nav_culture_pod'), icon: '🎧', action: handleCulturePodClick },
    { id: 'learning', label: t('learn'), icon: '📚', action: () => { window.history.pushState({}, '', '/learn'); setCurrentView('learning'); } },
    { id: 'verbExplorer', label: t('nav_verb_explorer'), icon: '📖', action: () => { window.history.pushState({}, '', '/verb-explorer'); setCurrentView('verbExplorer'); } },
    {
      id: 'dashboard',
      label: t('dashboard'),
      icon: '📊',
      submenu: [
        { id: 'dashboard-main', label: t('dashboard'), icon: '📊', action: () => { window.history.pushState({}, '', '/dashboard'); setCurrentView('dashboard'); } },
        { id: 'profile', label: t('profile'), icon: '👤', action: () => { window.history.pushState({}, '', '/profile'); setCurrentView('profile'); setInitialProfileTab('profile'); } },
        { id: 'membership', label: t('membership'), icon: '💎', action: () => { window.history.pushState({}, '', '/membership'); setCurrentView('profile'); setInitialProfileTab('membership'); } },
        { id: 'orders', label: t('orders'), icon: '📦', action: () => { window.history.pushState({}, '', '/orders'); setCurrentView('profile'); setInitialProfileTab('orders'); } },
      ]
    },
    { id: 'games', label: t('games'), icon: '🎮', action: () => { window.history.pushState({}, '', '/games'); setCurrentView('profile'); setInitialProfileTab('games'); } },
    { id: 'holidays', label: t('holidays'), icon: '🎉', action: () => { window.history.pushState({}, '', '/MoroccanHolidays'); setCurrentView('holidays'); } },
    { id: 'help', label: t('help'), icon: '❓', action: () => { window.history.pushState({}, '', '/help/documentation'); setCurrentView('profile'); setInitialProfileTab('help'); } },
  ];

  // Mobile hamburger menu - includes submenu structure
  const mainNavigationItems = [
    { id: 'translator', label: t('translation'), icon: '🌐', action: () => { window.history.pushState({}, '', '/translation'); setCurrentView('translator'); } },
    { id: 'dictionary', label: t('dictionary'), icon: '📖', action: () => { window.history.pushState({}, '', '/dictionary'); setCurrentView('dictionary'); } },
    { id: 'decoder', label: t('decoder_title'), icon: '🔮', action: () => { window.history.pushState({}, '', '/decoder'); setCurrentView('decoder'); } },
    { id: 'amazigh-translation', label: t('nav_amazigh'), icon: 'ⵣ', action: () => { window.history.pushState({}, '', '/amazigh-translation'); setCurrentView('amazigh-translation'); } },
    { id: 'my-daily-words', label: t('nav_daily_words'), icon: '📚', action: () => { window.history.pushState({}, '', '/my-daily-words'); setCurrentView('my-daily-words'); } },
    { id: 'traveltools', label: t('travel_tools'), icon: '🇲🇦', action: () => { window.history.pushState({}, '', '/travel-tools'); setCurrentView('traveltools'); } },
    { id: 'music', label: t('music'), icon: '🎵', action: handleMusicClick },
    { id: 'culturePod', label: t('nav_culture_pod'), icon: '🎧', action: handleCulturePodClick },
    { id: 'learning', label: t('learn'), icon: '📚', action: () => { window.history.pushState({}, '', '/learn'); setCurrentView('learning'); } },
    { id: 'verbExplorer', label: t('nav_verb_explorer'), icon: '📖', action: () => { window.history.pushState({}, '', '/verb-explorer'); setCurrentView('verbExplorer'); } },
    {
      id: 'dashboard',
      label: t('dashboard'),
      icon: '📊',
      submenu: [
        { id: 'dashboard-main', label: t('dashboard'), icon: '📊', action: () => { window.history.pushState({}, '', '/dashboard'); setCurrentView('dashboard'); } },
        { id: 'profile', label: t('profile'), icon: '👤', action: () => { window.history.pushState({}, '', '/profile'); setCurrentView('profile'); setInitialProfileTab('profile'); } },
        { id: 'membership', label: t('membership'), icon: '💎', action: () => { window.history.pushState({}, '', '/membership'); setCurrentView('profile'); setInitialProfileTab('membership'); } },
        { id: 'orders', label: t('orders'), icon: '📦', action: () => { window.history.pushState({}, '', '/orders'); setCurrentView('profile'); setInitialProfileTab('orders'); } },
      ]
    },
    { id: 'games', label: t('games'), icon: '🎮', action: () => { window.history.pushState({}, '', '/games'); setCurrentView('profile'); setInitialProfileTab('games'); } },
    { id: 'holidays', label: t('holidays'), icon: '🎉', action: () => { window.history.pushState({}, '', '/MoroccanHolidays'); setCurrentView('holidays'); } },
    { id: 'help', label: t('help'), icon: '❓', action: () => { window.history.pushState({}, '', '/help/documentation'); setCurrentView('profile'); setInitialProfileTab('help'); } },
  ];

  // Apply admin's icon overrides only — labels always come from t() so they
  // translate when the user switches language. Admin-typed English labels in
  // the design backend would otherwise block translation.
  const applyMenuOverrides = (items) => {
    if (!Object.keys(menuItemOverrides).length) return items;
    return items.map(item => {
      const override = menuItemOverrides[item.id];
      if (override) {
        return { ...item, icon: override.icon || item.icon, icon_url: override.icon_url || item.icon_url, icon_size: override.icon_size || item.icon_size };
      }
      return item;
    });
  };
  applyMenuOverrides(sidebarNavigationItems).forEach((item, i) => {
    sidebarNavigationItems[i].icon = item.icon;
    sidebarNavigationItems[i].icon_url = item.icon_url;
    sidebarNavigationItems[i].icon_size = item.icon_size;
  });
  applyMenuOverrides(mainNavigationItems).forEach((item, i) => {
    mainNavigationItems[i].icon = item.icon;
    mainNavigationItems[i].icon_url = item.icon_url;
    mainNavigationItems[i].icon_size = item.icon_size;
  });

  // Apply submenu overrides (labels, icons, order) from admin
  const applySubmenuOverrides = (items) => {
    if (!Object.keys(submenuOverrides).length) return;
    items.forEach(item => {
      if (!item.submenu) return;
      const parentOverride = submenuOverrides[item.id];
      if (!parentOverride) return;
      // Apply icon overrides only — labels stay from t() so they translate
      if (parentOverride.items) {
        item.submenu = item.submenu.map(sub => {
          const subOverride = parentOverride.items[sub.id];
          if (subOverride) {
            return { ...sub, icon: subOverride.icon || sub.icon, icon_url: subOverride.icon_url || sub.icon_url };
          }
          return sub;
        });
      }
      // Apply order
      if (parentOverride.order?.length) {
        const orderMap = {};
        parentOverride.order.forEach((id, idx) => { orderMap[id] = idx; });
        item.submenu = [...item.submenu].sort((a, b) => {
          const aIdx = orderMap[a.id] ?? 999;
          const bIdx = orderMap[b.id] ?? 999;
          return aIdx - bIdx;
        });
      }
    });
  };
  applySubmenuOverrides(sidebarNavigationItems);
  applySubmenuOverrides(mainNavigationItems);

  if (isAdminAuthenticated) {
    mainNavigationItems.push({ id: 'admin', label: t('admin'), icon: '⚙️' });
  }

  // Filter navigation items based on visibility settings
  const filterNavigationItem = (item) => {
    // Admins always see everything
    if (isAdminAuthenticated) return true;

    // Check My Morocco visibility
    if (item.id === 'traveltools') {
      // If hidden, don't show to anyone
      if (!travelToolsSettings.is_visible) return false;
      // Pro-only check handled in MyMorocco component (shows upgrade prompt)
    }

    // Check Music visibility
    if (item.id === 'music') {
      if (!musicSettings.pageVisible) return false;
    }

    // Check Culture Pod visibility
    if (item.id === 'culturePod') {
      if (!culturePodSettings.pageVisible) return false;
    }

    // Check Learning visibility
    if (item.id === 'learning') {
      if (appSettings.learningEnabled === false) return false;
    }

    // Check Decoder visibility (controlled via admin decoder settings)
    if (item.id === 'decoder') {
      if (!decoderSettings.is_visible) return false;
    }

    // Check Dictionary visibility (controlled via admin dictionary settings)
    if (item.id === 'dictionary') {
      if (!dictionarySettings.is_visible) return false;
    }

    // Check Amazigh Translation visibility
    if (item.id === 'amazigh-translation') {
      if (!amazighSettings.is_visible) return false;
    }

    // Check Verb Explorer visibility
    if (item.id === 'verbExplorer') {
      if (!verbExplorerSettings.is_visible) return false;
    }

    // Check Translation visibility
    if (item.id === 'translator') {
      if (appSettings.translationPageVisible === false) return false;
    }

    return true;
  };

  // Apply menu order from nav design settings
  const applyMenuOrder = (items) => {
    if (!navDesign?.menu_order?.length) return items;
    const orderMap = {};
    navDesign.menu_order.forEach((id, idx) => { orderMap[id] = idx; });
    return [...items].sort((a, b) => {
      const aIdx = orderMap[a.id] ?? 999;
      const bIdx = orderMap[b.id] ?? 999;
      return aIdx - bIdx;
    });
  };

  // Apply order then filter to sidebar navigation
  const filteredSidebarItems = applyMenuOrder(sidebarNavigationItems).filter(filterNavigationItem);
  const filteredMainNavItems = applyMenuOrder(mainNavigationItems).filter(filterNavigationItem);

  // Bottom Mobile Navigation Items - Each opens its own page
  const allBottomNavItems = [
    {
      id: 'daily-words',
      label: t('explore') || 'Explore',
      icon: '🎧',
      icon_url: appSettings.explore_icon_url || '',
      requiresPaidPlan: true,
      onClick: () => {
        setCurrentView('daily-words');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    {
      id: 'favorites',
      label: t('favorites'),
      icon: '❤️',
      icon_url: appSettings.favorites_icon_url || '',
      requiresPaidPlan: true,
      onClick: () => {
        if (!userToken) {
          showNotification(t('login_required_favorites'), 'info');
        } else if (!hasPaidPlan) {
          showNotification(t('upgrade_pro_favorites'), 'info');
          setCurrentView('profile');
          setInitialProfileTab('membership');
        } else {
          setCurrentView('favorites');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    {
      id: 'request',
      label: t('request'),
      icon: '➕',
      icon_url: appSettings.request_word_icon_url || '',
      requiresPaidPlan: true,
      onClick: () => {
        if (!userToken) {
          showNotification(t('login_required_request'), 'info');
        } else if (!hasPaidPlan) {
          showNotification(t('upgrade_pro_request'), 'info');
          setCurrentView('profile');
          setInitialProfileTab('membership');
        } else {
          setCurrentView('request');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    {
      id: 'progress',
      label: t('word_bank'),
      icon: '\u{1F4DA}',
      icon_url: appSettings.word_bank_icon_url || '',
      requiresPaidPlan: true,
      onClick: () => {
        if (!userToken) {
          showNotification(t('login_required_progress'), 'info');
        } else if (!hasPaidPlan) {
          showNotification(t('upgrade_pro_progress'), 'info');
          setCurrentView('profile');
          setInitialProfileTab('membership');
        } else {
          setCurrentView('progress');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    // Removed: 'categories' nav item (deprecated feature, removed from product long ago)
  ];

  // Filter bottom nav items based on settings and user role
  const bottomNavItems = allBottomNavItems.filter(item => {
    // Admins always see everything
    if (isAdminAuthenticated) {
      return true;
    }
    
    // Check feature-specific visibility for regular users
    if (item.id === 'progress') {
      // Word Bank is visible if both progressTrackingEnabled AND badgesEnabled are true
      return appSettings.progressTrackingEnabled === true && appSettings.badgesEnabled === true;
    }
    if (item.id === 'daily-words') {
      // Feelings is visible if both dailyWordsEnabled AND moodCategoriesEnabled are true
      return appSettings.dailyWordsEnabled === true && appSettings.moodCategoriesEnabled === true;
    }
    if (item.id === 'holidays') {
      return appSettings.holidaysEnabled !== false;
    }
    return true;
  });

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } border-b shadow-lg`}>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-lg ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Logo size="sm" dark={isDarkMode} />

          </div>
          
          {showLoggedInChrome && (
            <div className="flex items-center gap-1.5">
              {/* Weather + Time Display - Mobile */}
              {weatherData && weatherSettings.enabled && (
                <div className="appshell-weather flex items-center gap-1 px-1.5 py-1 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg text-white text-[10px] font-semibold">
                  <span>{weatherData.icon} {weatherData.temp_c}° {cityAbbrev[weatherData.city] || weatherData.city.slice(0,4)}</span>
                  <span className="opacity-50">|</span>
                  <span className="appshell-time">⏰ {moroccoTime} (MA)</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sidebar - Desktop */}
      <div className={`hidden lg:flex fixed left-0 top-0 bottom-0 z-40 text-white shadow-2xl transition-all duration-300`}
        style={{ ...sidebarBgStyle, width: isSidebarCollapsed ? navCollapsedWidth : navSidebarWidth }}>
        <div className="flex flex-col w-full">
          {/* Logo */}
          <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700/50' : 'border-blue-500/30'}`}>
            <div className="flex items-center justify-between">
              {!isSidebarCollapsed && (
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <img src="/logo.svg" alt="DarijaPro" className="h-12" />
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-blue-200'} text-sm mt-1`}>{t('language_portal')}</p>
                  {/* Weather Display - Desktop Sidebar */}
                  {showLoggedInChrome && weatherData && weatherSettings.enabled && (
                    <div className="appshell-weather flex items-center gap-1.5 mt-1 text-white/80 text-sm">
                      <span>{weatherData.icon}</span>
                      <span>{cityAbbrev[weatherData.city] || weatherData.city}</span>
                      <span className="font-medium">{weatherData.temp_c}°C</span>
                    </div>
                  )}
                  {/* Morocco Time - Desktop Sidebar */}
                  {showLoggedInChrome && (
                    <div className="flex items-center gap-1.5 mt-1 text-white/70 text-sm">
                      <span>⏰</span>
                      <span className="appshell-time">{moroccoTime}</span>
                      <span className="text-xs opacity-70">(Morocco)</span>
                    </div>
                  )}
                  {/* Holiday Notification - Desktop Sidebar (next to weather/time) */}
                  {holidayNotifications.has_notifications && holidayNotifications.holidays.length > 0 && (
                    <button
                      onClick={() => {
                        setCurrentView('holidays');
                        if (holidayNotifications.holidays.length > 0) {
                          navigate(`?highlight=${holidayNotifications.holidays[0].id}`);
                        }
                      }}
                      className="relative mt-2 w-full p-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-all group"
                      title={t('holiday_details_tooltip')}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl animate-bounce">{holidayPageSettings.notification_icon_url ? <img src={`${BACKEND_URL}${holidayPageSettings.notification_icon_url}`} alt="" className="w-6 h-6 object-contain" /> : '🎉'}</span>
                        <div className="flex-1 text-left">
                          <p className="text-white text-xs font-bold truncate">
                            {holidayNotifications.holidays[0].name_en}
                          </p>
                          <p className={`${isDarkMode ? 'text-gray-400' : 'text-blue-200/80'} text-[10px]`}>
                            {holidayNotifications.holidays[0].days_until === 0
                              ? <>{holidayPageSettings.highlight_confetti_icon_url ? <img src={`${BACKEND_URL}${holidayPageSettings.highlight_confetti_icon_url}`} alt="" className="w-3 h-3 object-contain inline" /> : '🎊'} {t('today_excl')}</>
                              : `${holidayNotifications.holidays[0].days_until} days`}
                            {holidayNotifications.count > 1 && ` +${holidayNotifications.count - 1} more`}
                          </p>
                        </div>
                        <span className="text-white/60 text-xs group-hover:text-white/90">→</span>
                      </div>
                    </button>
                  )}
                </div>
              )}
              {isSidebarCollapsed && holidayNotifications.has_notifications && (
                <button
                  onClick={() => {
                    setCurrentView('holidays');
                    if (holidayNotifications.holidays.length > 0) {
                      navigate(`?highlight=${holidayNotifications.holidays[0].id}`);
                    }
                  }}
                  className="relative mx-auto"
                  title={`${holidayNotifications.count} ${holidayNotifications.count > 1 ? t('upcoming_holidays_many') : t('upcoming_holiday_one')}`}
                >
                  <div className="text-xl animate-bounce">{holidayPageSettings.notification_icon_url ? <img src={`${BACKEND_URL}${holidayPageSettings.notification_icon_url}`} alt="" className="w-6 h-6 object-contain" /> : '🎉'}</div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-bold animate-pulse">
                    {holidayNotifications.count}
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* User Info */}
          {currentUser && !isSidebarCollapsed && (
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700/50' : 'border-blue-500/30'}`}>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/10 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-2xl overflow-hidden" style={{width:48,height:48,flexShrink:0,borderRadius:'9999px'}}>
                  {(() => {
                    const raw = currentUser.avatar?.image_url;
                    const imgUrl = raw?.startsWith('/') ? `${BACKEND_URL}${raw}` : raw?.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
                    return imgUrl ? (
                      <img
                        src={imgUrl}
                        alt="Avatar"
                        width="48"
                        height="48"
                        loading="eager"
                        decoding="async"
                        className="w-full h-full object-cover object-top"
                        style={{width:48,height:48,objectFit:'cover'}}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null;
                  })()}
                  <span className={`${currentUser.avatar?.image_url ? 'hidden' : 'flex'}`}>
                    {currentUser.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{currentUser.full_name}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-blue-200'} truncate`}>{currentUser.email}</p>
                  {userPlan && (
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold mt-1 ${
                      userPlan.name !== 'free'
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-blue-500/30 text-blue-100'
                    }`}>
                      {userPlan.display_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {filteredSidebarItems.map((item) => {
              // Check if this item is active
              const isActive = currentView === item.id || (currentView === 'profile' && activeProfileTab === item.id);
              
              // Determine if this item's submenu is open
              const isSubmenuOpen = item.id === 'dashboard' ? isDashboardSubmenuOpen : 
                                   item.id === 'help' ? isHelpSubmenuOpen : false;
              
              return (
                <div key={item.id}>
                  <button
                    onClick={() => {
                      if (item.submenu) {
                        if (item.id === 'dashboard') {
                          setIsDashboardSubmenuOpen(!isDashboardSubmenuOpen);
                        } else if (item.id === 'help') {
                          setIsHelpSubmenuOpen(!isHelpSubmenuOpen);
                        }
                      } else if (item.route) {
                        navigate(item.route);
                      } else if (item.action) {
                        item.action();
                      } else {
                        setCurrentView(item.id);
                      }
                    }}
                    className={`appshell-menu-item w-full flex items-center gap-4 px-4 py-3 transition-all group ${
                      navActiveStyle === 'full-width' ? 'rounded-lg' : 'rounded-xl'
                    } ${
                      isActive
                        ? navActiveStyle === 'left-border' ? 'border-l-4 shadow-lg' : 'shadow-xl scale-105'
                        : ''
                    }`}
                    style={isActive ? activeItemStyle : { color: nd ? navTextColor : undefined, ...(navHoverBg ? {} : {}) }}
                  >
                    {(() => {
                      const itemSize = cssSize(item.icon_size) || globalIconSize || '24px';
                      const expandedSize = cssSize(item.icon_size) || globalIconSize || '24px';
                      const collapsedSize = globalIconSizeCollapsed || '14px';
                      const activeSize = isSidebarCollapsed ? collapsedSize : itemSize;
                      return (
                        <div className={`appshell-icon-slot ${isSidebarCollapsed ? 'mx-auto' : ''}`} style={{fontSize: activeSize, width: activeSize, height: activeSize, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                          {item.icon_url && <img src={`${BACKEND_URL}${item.icon_url}`} alt="" width="24" height="24" loading="eager" decoding="async" style={{width: isSidebarCollapsed ? collapsedSize : expandedSize, height: isSidebarCollapsed ? collapsedSize : expandedSize, display:'block'}} />}
                        </div>
                      );
                    })()}
                    {!isSidebarCollapsed && (
                      <>
                        <span className="font-semibold">{item.label}</span>
                        {item.submenu && (
                          <svg
                            className={`w-4 h-4 ml-auto transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </>
                    )}
                    {isActive && !isSidebarCollapsed && !item.submenu && (
                      <div className={`ml-auto w-2 h-2 rounded-full ${isDarkMode ? 'bg-purple-400' : 'bg-blue-600'} animate-pulse`}></div>
                    )}
                  </button>

                  {/* Submenu */}
                  {item.submenu && isSubmenuOpen && !isSidebarCollapsed && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.submenu.map((subItem) => (
                        <button
                          key={subItem.id}
                          onClick={() => {
                            subItem.action();
                          }}
                          className={`w-full text-left px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'text-gray-400 hover:bg-gray-700/50 hover:text-white' : 'text-blue-200 hover:bg-white/10 hover:text-white'} transition-all flex items-center gap-2`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isDarkMode ? 'bg-gray-500' : 'bg-blue-300'}`}></span>
                          <span style={{display:'inline-flex',width:'1.25em',height:'1.25em',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            {subItem.icon_url && <img src={`${BACKEND_URL}${subItem.icon_url}`} alt="" width="20" height="20" loading="eager" decoding="async" style={{width:'1.25em',height:'1.25em',objectFit:'contain'}} />}
                          </span>
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Dark Mode Toggle */}
          <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700/50' : 'border-blue-500/30'}`}>
            <button
              onClick={onToggleDarkMode}
              className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl ${isDarkMode ? 'text-gray-300 hover:bg-gray-700/50' : 'text-blue-100 hover:bg-white/10'} transition-all ${
                isSidebarCollapsed ? 'justify-center' : ''
              }`}
              title={isDarkMode ? t('switch_to_light_mode') : t('switch_to_dark_mode')}
            >
              {isDarkMode ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
              {!isSidebarCollapsed && (
                <span className="font-semibold">
                  {isDarkMode ? t('light_mode_label') : t('dark_mode_label')}
                </span>
              )}
            </button>
          </div>

          {/* Logout Button */}
          {currentUser && (
            <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700/50' : 'border-blue-500/30'}`}>
              <button
                onClick={onLogout}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl ${isDarkMode ? 'text-gray-300' : 'text-blue-100'} hover:bg-red-500/20 hover:text-white transition-all ${
                  isSidebarCollapsed ? 'justify-center' : ''
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {!isSidebarCollapsed && <span className="font-semibold">{t('logout')}</span>}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar Overlay — always mounted; toggles via CSS to keep icons cached */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <div
          className={`fixed left-0 top-0 bottom-16 w-80 text-white shadow-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
          style={sidebarBgStyle}
          onClick={e => e.stopPropagation()}
        >
            <div className="flex flex-col h-full">
              {/* Mobile Header */}
              <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700/50' : 'border-blue-500/30'}`}>
                <div className="flex items-center justify-between mb-4">
                  <Logo size="md" dark={true} />
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`p-2 ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-blue-500/30'} rounded-lg`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Holiday Placeholder - Mobile */}
                {holidayNotifications.has_notifications && holidayNotifications.holidays.length > 0 && (
                  <button
                    onClick={() => {
                      setCurrentView('holidays');
                      setIsMobileMenuOpen(false);
                      if (holidayNotifications.holidays[0]?.id) {
                        navigate(`?highlight=${holidayNotifications.holidays[0].id}`);
                      }
                    }}
                    className="w-full mb-4 p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl animate-bounce">{holidayPageSettings.notification_icon_url ? <img src={`${BACKEND_URL}${holidayPageSettings.notification_icon_url}`} alt="" className="w-7 h-7 object-contain" /> : '🎉'}</span>
                      <div className="flex-1 text-left">
                        <p className="text-white text-sm font-bold">
                          {holidayNotifications.holidays[0].name_en}
                        </p>
                        <p className={`${isDarkMode ? 'text-gray-400' : 'text-blue-200/80'} text-xs`}>
                          {holidayNotifications.holidays[0].days_until === 0
                            ? <>{holidayPageSettings.highlight_confetti_icon_url ? <img src={`${BACKEND_URL}${holidayPageSettings.highlight_confetti_icon_url}`} alt="" className="w-4 h-4 object-contain inline" /> : '🎊'} Today!</>
                            : `In ${holidayNotifications.holidays[0].days_until} days`}
                          {holidayNotifications.count > 1 && ` • +${holidayNotifications.count - 1} more`}
                        </p>
                      </div>
                      <span className="text-white/60">→</span>
                    </div>
                  </button>
                )}
                
                {currentUser && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/10">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-2xl overflow-hidden" style={{width:48,height:48,flexShrink:0,borderRadius:'9999px'}}>
                      {(() => {
                        const raw = currentUser.avatar?.image_url;
                        const imgUrl = raw?.startsWith('/') ? `${BACKEND_URL}${raw}` : raw?.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
                        return imgUrl ? (
                          <img
                            src={imgUrl}
                            alt="Avatar"
                            width="48"
                            height="48"
                            loading="eager"
                            decoding="async"
                            className="w-full h-full object-cover object-top"
                            style={{width:48,height:48,objectFit:'cover'}}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null;
                      })()}
                      <span className={`${currentUser.avatar?.image_url ? 'hidden' : 'flex'}`}>
                        {currentUser.full_name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{currentUser.full_name}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-blue-200'}`}>{currentUser.email}</p>
                      {/* Membership Status - Mobile */}
                      {userPlan && (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold mt-1 ${
                          userPlan.name !== 'free'
                            ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                            : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-blue-500/30 text-blue-100'
                        }`}>
                          {userPlan.display_name}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto pb-20">
                {filteredMainNavItems.map((item) => {
                  // Check if this item is active (same logic as desktop sidebar)
                  const isActive = item.id === 'translator' 
                    ? currentView === 'translator'
                    : currentView === 'profile' && activeProfileTab === item.id;
                  
                  // Determine if this item's submenu is open
                  const isSubmenuOpenMobile = item.id === 'dashboard' ? isDashboardSubmenuOpenMobile : 
                                              item.id === 'help' ? isHelpSubmenuOpenMobile : false;
                  
                  return (
                  <div key={item.id}>
                    <button
                      onClick={() => {
                        if (item.submenu) {
                          if (item.id === 'dashboard') {
                            setIsDashboardSubmenuOpenMobile(!isDashboardSubmenuOpenMobile);
                          } else if (item.id === 'help') {
                            setIsHelpSubmenuOpenMobile(!isHelpSubmenuOpenMobile);
                          }
                        } else if (item.route) {
                          navigate(item.route);
                          setIsMobileMenuOpen(false);
                        } else if (item.action) {
                          item.action();
                          setIsMobileMenuOpen(false);
                        } else {
                          setCurrentView(item.id);
                          setIsMobileMenuOpen(false);
                        }
                      }}
                      className={`appshell-menu-item w-full flex items-center justify-between px-4 py-3 transition-all ${
                        navActiveStyle === 'full-width' ? 'rounded-lg' : 'rounded-xl'
                      } ${
                        item.color ? item.color :
                        isActive
                          ? navActiveStyle === 'left-border' ? 'border-l-4 shadow-lg' : 'shadow-xl'
                          : ''
                      }`}
                      style={isActive && !item.color ? activeItemStyle : (nd ? { color: navTextColor } : undefined)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="appshell-icon-slot" style={{fontSize: cssSize(item.icon_size) || globalIconSize || '20px', width: cssSize(item.icon_size) || globalIconSize || '20px', height: cssSize(item.icon_size) || globalIconSize || '20px', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>{item.icon_url && <img src={`${BACKEND_URL}${item.icon_url}`} alt="" width="20" height="20" loading="eager" decoding="async" style={{width: cssSize(item.icon_size) || globalIconSize || '20px', height: cssSize(item.icon_size) || globalIconSize || '20px', objectFit:'contain',display:'inline-block',verticalAlign:'middle'}} />}</span>
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {item.submenu && (
                        <svg 
                          className={`w-4 h-4 transition-transform ${isSubmenuOpenMobile ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    {/* Submenu */}
                    {item.submenu && isSubmenuOpenMobile && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.submenu.map((subItem) => (
                          <button
                            key={subItem.id}
                            onClick={() => {
                              subItem.action();
                              setIsMobileMenuOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'text-gray-400 hover:bg-gray-700/50 hover:text-white' : 'text-blue-200 hover:bg-white/10 hover:text-white'} transition-all flex items-center gap-2`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${isDarkMode ? 'bg-gray-500' : 'bg-blue-300'}`}></span>
                            <span style={{display:'inline-flex',width:'1.25em',height:'1.25em',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                              {subItem.icon_url && <img src={`${BACKEND_URL}${subItem.icon_url}`} alt="" width="20" height="20" loading="eager" decoding="async" style={{width:'1.25em',height:'1.25em',objectFit:'contain'}} />}
                            </span>
                            {subItem.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

      {/* Main Content */}
      <style>{`@media (min-width: 1024px) { .nav-content-area { margin-left: ${isSidebarCollapsed ? navCollapsedWidth : navSidebarWidth}px !important; } }`}</style>
      <div className="nav-content-area pt-16 lg:pt-0 pb-24 lg:pb-0 transition-all duration-300">
        <div className="min-h-[calc(100vh-10rem)] lg:min-h-screen">
          {children}
        </div>
      </div>

      {/* Bottom Mobile Navigation — hidden while typing so iOS keyboard doesn't sit behind it */}
      <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } border-t shadow-2xl h-14 ${isInputFocused ? 'hidden' : ''}`}>
        <div className="flex items-center justify-around px-1 py-1 h-full">
          {bottomNavItems.map((item) => (
            <div key={item.id} className="relative">
              <button
                onClick={() => {
                  // Check if feature requires premium and user doesn't have it
                  if (item.requiresPaidPlan && !hasPaidPlan) {
                    if (!userToken) {
                      showNotification(`Please login to use ${item.label}`, 'info');
                    } else {
                      showNotification(`Upgrade to Pro to access ${item.label}`, 'info');
                    }
                    setInitialProfileTab('membership');
                    setCurrentView('profile');
                    return;
                  }
                  item.onClick();
                }}
                className={`flex flex-col items-center gap-0.5 px-2 py-2 min-w-[60px] rounded-lg transition-all ${
                  currentView === item.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg scale-105'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <span className="text-sm" style={{display:'inline-flex',width:'1.5em',height:'1.5em',alignItems:'center',justifyContent:'center',flexShrink:0}}>{item.icon_url && <img src={`${BACKEND_URL}${item.icon_url}`} alt="" width="24" height="24" loading="eager" decoding="async" style={{width:'1.5em',height:'1.5em',objectFit:'contain',display:'inline-block',verticalAlign:'middle'}} />}</span>
                <span className="text-[10px] font-medium leading-tight">{item.label}</span>
              </button>
              {/* Lock badge for premium features if user doesn't have paid plan */}
              {item.requiresPaidPlan && !hasPaidPlan && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full w-4 h-4 flex items-center justify-center shadow-lg">
                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Darija Buddy - Global Interactive Bot (hidden on music page) */}
      {currentView !== 'music' && currentView !== 'learning' && !activeGame && (
        <DarijaBuddy
          API={BACKEND_URL + '/api'}
          userToken={userToken}
          hasPaidPlan={hasPaidPlan}
          currentMood={currentMood}
          currentPage={currentView}
          isDarkMode={isDarkMode}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

export default ModernLayout;
